import { useId, useRef, useState } from 'react';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider } from '@mui/material';
import { PlusFillIcon } from 'src/assets/icons/PlusFillIcon';
import { AggregationSelection, AxisItemConfig, StatisticSelection } from 'src/types/trends/trends';
import { GetAllResponse } from 'src/services/sites/types';
import { SelectedInputType } from '../../types/selected-input';
import { TimelineChartValue } from '../../types';
import { AxisDialogItem } from '../axis-dialog-item/axis-dialog-item';

type AddChartModalContentProps = {
  onToggleHandler: () => void;
  updatedSetValues?: (values: TimelineChartValue, isTimelineChart: boolean) => void;
  addNewItemToExistingChart?: (values: TimelineChartValue) => void;
  onSelectEventHandler?: (selectedInputs: SelectedInputType[], id: string) => void;
  isParticularChart?: boolean;
  chartId?: string;
  selectedInputs?: SelectedInputType[];
  sites?: { value: GetAllResponse[]; loaded: boolean };
  fixedSiteId?: string;
};

const AddChartModalContent = ({
  onToggleHandler,
  onSelectEventHandler,
  isParticularChart,
  chartId,
  selectedInputs,
  fixedSiteId,
}: AddChartModalContentProps) => {
  const id = useId();
  const rowIdRef = useRef(0);

  const emptyRow = (): AxisItemConfig => ({
    id: String(rowIdRef.current++),
    siteId: fixedSiteId ?? '',
    siteName: '',
    tagId: '',
    inputName: '',
    rawInputName: '',
    gatewayId: '',
    unit: '',
    dataType: '',
    color: '#1976d2',
    aggregation: 'Auto' as AggregationSelection,
    statistic: 'Auto' as StatisticSelection,
  });

  const [configuredTags, setConfiguredTags] = useState<AxisItemConfig[]>(() => {
    if (selectedInputs && selectedInputs.length > 0) {
      return selectedInputs.map((input) => ({
        id: String(rowIdRef.current++),
        siteId: input.siteId || fixedSiteId || '',
        siteName: input.siteName || '',
        tagId: input.inputId,
        inputName: input.inputName,
        rawInputName: input.rawInputName,
        gatewayId: input.gatewayId,
        unit: input.unit,
        dataType: input.dataType || '',
        color: input.color ?? '#1976d2',
        aggregation: (input.aggregation as AggregationSelection) ?? 'Auto',
        statistic: (input.statistic as StatisticSelection) ?? 'Auto',
        yAxisAuto: input.yAxisAuto,
        yAxisFrom: input.yAxisFrom,
        yAxisTo: input.yAxisTo,
      }));
    }
    return [emptyRow()];
  });

  const onChangeItem = (id: string, patch: Partial<AxisItemConfig>) => {
    setConfiguredTags((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  };

  const onRemoveTag = (id: string) => {
    setConfiguredTags((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSave = () => {
    if (onSelectEventHandler) {
      const inputs: SelectedInputType[] = configuredTags
        .filter((tag) => tag.tagId && tag.siteId)
        .map((tag) => ({
          siteId: tag.siteId,
          siteName: tag.siteName,
          gatewayId: tag.gatewayId,
          inputId: tag.tagId,
          inputName: tag.inputName,
          rawInputName: tag.rawInputName,
          unit: tag.unit,
          dataType: tag.dataType,
          color: tag.color,
          aggregation: tag.aggregation,
          statistic: tag.statistic,
          yAxisAuto: tag.yAxisAuto,
          yAxisFrom: tag.yAxisFrom,
          yAxisTo: tag.yAxisTo,
        }));
      onSelectEventHandler(inputs, isParticularChart ? chartId! : id);
    }
    onToggleHandler();
  };

  return (
    <>
      <DialogTitle>Edit Trend</DialogTitle>
      <Divider />
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', padding: '2rem 1rem', gap: '12px' }}
      >
        {configuredTags?.map((item) => (
          <AxisDialogItem
            key={item.id}
            inputId={item.id}
            siteId={item.siteId}
            tagId={item.tagId}
            aggregation={item.aggregation}
            onChangeItem={onChangeItem}
            onRemove={onRemoveTag}
            color={item.color}
            canRemove={configuredTags?.length > 1}
            yAxisAuto={item.yAxisAuto}
            yAxisFrom={item.yAxisFrom}
            yAxisTo={item.yAxisTo}
            fixedSiteId={fixedSiteId}
            statistic={item.statistic}
          />
        ))}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<PlusFillIcon />}
          onClick={() => setConfiguredTags((prev) => [...prev, emptyRow()])}
        >
          Add new
        </Button>
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Button variant="outlined" onClick={onToggleHandler}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

export default AddChartModalContent;
